/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { func, bool, array } from 'prop-types';
import update from 'immutability-helper';
import isEqual from 'lodash/isEqual';
import filter from 'lodash/filter';

import { CHECKBOX, SELECT, RANGE } from 'actions/search';

/**
 * This is the TalentsFilter component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class TalentsFilter extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    children: func.isRequired,
    onSubmit: func.isRequired,
    sections: array,
    loading: bool,
  };

  static defaultProps = {
    sections: [],
    loading: false,
  };

  /**
   * Create TalentsFilter.
   */
  constructor(props) {
    super(props);

    const { sections } = props;
    const form = this.mapFormData(sections);

    this.state = {
      form,
      query: '',
      selectedSection: null,
      firstRender: true,
    };
  }

  componentDidMount() {
    this.setState({ firstRender: false });
  }

  componentDidUpdate(prevProps) {
    const { sections: prevSections } = prevProps;
    const { sections } = this.props;

    if (!isEqual(sections, prevSections)) {
      const form = this.mapFormData(sections);

      this.setState({ form });
    }
  }

  mapFormData = (sections, willClearForm = false) => {
    const form = {};
    // onClear set filter to initial prop values
    const SECTIONS = willClearForm ? this.props.sections : sections;

    SECTIONS.map(({ tag: section, selectors }) => {
      selectors.map(
        ({
          name,
          type,
          select_tags,
          checkbox_tag,
          checkbox_active,
          range_left_param,
          range_left_value,
          range_right_param,
          range_right_value,
        }) => {
          // Map data for checkboxes.
          if (type === CHECKBOX) {
            form[name] = {
              section,
              type,
              tag: checkbox_tag,
              value: checkbox_active,
            };

            // Map data for selects.
          } else if (type === SELECT) {
            form[name] = {
              section,
              type,
              value: select_tags.find(s => s.is_active).tag,
            };

            // Map data for range sliders.
          } else if (type === RANGE) {
            form[name] = {
              section,
              type,
              leftParam: range_left_param,
              rightParam: range_right_param,
              [range_left_param]: range_left_value,
              [range_right_param]: range_right_value,
            };
          }
        }
      );
    });

    return form;
  };

  submitHandler = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { form, selectedSection } = this.state;
    const inputs = filter(form, {
      section: selectedSection,
    });

    const filters = [];
    const params = {};

    inputs.map(({ type, tag, value, leftParam, rightParam }, index) => {
      if (type === CHECKBOX && value === true) {
        filters.push(tag);
      }

      if (type === SELECT) {
        filters.push(value);
      }

      // Special for the range data.
      if (type === RANGE) {
        params[leftParam] = inputs[index][leftParam];
        params[rightParam] = inputs[index][rightParam];
      }
    });

    this.onSubmit(filters, params);
  };

  onSubmit = (inputs = null, params = {}) => {
    const { query, selectedSection } = this.state;
    const { loading, onSubmit } = this.props;

    if (loading) return;

    const filters = [...selectedSection, ...inputs].join(',');

    onSubmit({
      query,
      filters,
      ...params,
    });
  };

  onChange = (name, value, key, type) => {
    const { form } = this.state;

    this.setState(
      {
        form: update(form, {
          [name]: {
            [key]: { $set: value },
          },
        }),
      },
      () => {
        // If checkbox or select we submit directly.
        if (type === CHECKBOX || type === SELECT) {
          this.submitHandler();
        }
      }
    );
  };

  onChangeSearchField = event => {
    const { value } = event.target;

    this.setState({
      query: value,
    });
  };

  onClickClear = () => {
    const { selectedSection } = this.state;
    const { loading, sections, onSubmit } = this.props;

    if (loading) return;

    const form = this.mapFormData(sections, true);

    this.setState({ form, query: '' });

    onSubmit({
      query: null,
      filters: selectedSection,
    });
  };

  hideFilter = () => this.setState({ selectedSection: null });

  toggleSection = (event, tag) => {
    event.preventDefault();

    const { selectedSection } = this.state;

    const isActive = selectedSection === tag;

    this.setState(
      {
        selectedSection: isActive ? null : tag,
      },
      this.onSubmit
    );
  };

  /**
   * Render TalentsFilter.
   *
   * @return {Object}
   */

  render() {
    const { form, query, firstRender, selectedSection } = this.state;
    const { loading, sections, children } = this.props;

    return (
      <React.Fragment>
        {children({
          query,
          firstRender,
          form,
          selectedSection,
          submitHandler: this.submitHandler,
          onChangeSearchField: this.onChangeSearchField,
          onClickClear: this.onClickClear,
          toggleSection: this.toggleSection,
          onChange: this.onChange,
          loading,
          sections,
          hideFilter: this.hideFilter,
        })}
      </React.Fragment>
    );
  }
}
